/**
 * Google Ads dynamic insertion tokens.
 *
 * Advertisers embed placeholders that Google resolves at auction time —
 * `Toilet Unblocking in {LOCATION(City):Kent}` serves the searcher's own city and falls back
 * to `Kent`. Google validates length against the expanded fallback, not the raw template.
 *
 * Only the tag families below are recognised. Anything else in braces (countdown, ValueTrack,
 * an unrecognised future tag) is left intact so its raw length is counted — never becoming more
 * permissive than Google on a rule that has not been confirmed against the API.
 *
 * Mirrors backend/app/utils/ad_copy_tokens.py — keep the two in step.
 */

export const HEADLINE_MAX_LENGTH = 30;
export const DESCRIPTION_MAX_LENGTH = 90;

const TAG_BODY = String.raw`keyword|location\((?:city|state|country)\)|customizer\.[\w-]+|if\([^(){}]*\)`;

const TAG_WITH_FALLBACK = new RegExp(String.raw`\{(?:${TAG_BODY}):([^{}]*)\}`, 'gi');
const TAG_WITHOUT_FALLBACK = new RegExp(String.raw`\{(?:${TAG_BODY})\}`, 'i');
const ANY_TAG = new RegExp(String.raw`\{(?:${TAG_BODY})(?::[^{}]*)?\}`, 'gi');

/** The text as a searcher would see it, with each recognised tag replaced by its fallback. */
export function effectiveText(text: string): string {
  return text.replace(TAG_WITH_FALLBACK, '$1');
}

/** The length Google measures against its character limits. */
export function effectiveLength(text: string): number {
  return effectiveText(text).length;
}

/** True when a recognised tag carries no fallback, so Google will not count the asset. */
export function hasUnfallbackedTag(text: string): boolean {
  return TAG_WITHOUT_FALLBACK.test(text);
}

/**
 * A comparison key that case-folds literal text but preserves tags verbatim.
 *
 * `{KeyWord:}` and `{keyword:}` select different capitalisation of the inserted term, so they
 * are distinct assets to Google even though the surrounding words match.
 */
export function dedupeKey(text: string): string {
  const parts: string[] = [];
  let cursor = 0;
  for (const match of text.matchAll(ANY_TAG)) {
    parts.push(text.slice(cursor, match.index).toLowerCase());
    parts.push(match[0]);
    cursor = match.index + match[0].length;
  }
  parts.push(text.slice(cursor).toLowerCase());
  return parts.join('').replace(/\s+/g, ' ').trim();
}
